import React from 'react';
import { connect } from 'react-redux';
import { createStructuredSelector } from 'reselect';
import get from 'lodash.get';

import { AppState } from 'services/types';
import {
    VirtualizedTableErrorMessage,
    VirtualizedTableItemValue,
    VirtualizedTableProps
} from 'services/virtualizedTable/types';
import { setItemValue } from 'services/virtualizedTable/actions';
import {
    makeSelectVirtualizedTableItemValue,
    makeSelectVirtualizedTableErrorMessage
} from 'services/virtualizedTable/selectors';
import { toNumber, toNull, toNumberIfValueNotNaN } from 'forms/normalizers';

import { VirtualizedTableOnItemValueChange } from '../../types';

interface OwnProps extends Required<VirtualizedTableProps> {
    number?: boolean;
    onItemValueChange?: VirtualizedTableOnItemValueChange;
}

interface ConnectedProps {
    itemValue?: VirtualizedTableItemValue;
    errorMessage?: VirtualizedTableErrorMessage;
}

interface DispatchProps {
    setItemValue: typeof setItemValue;
}

type Props = OwnProps & ConnectedProps & DispatchProps;

interface State {
    initialItemValue: VirtualizedTableItemValue;
    inputValue: VirtualizedTableItemValue;
}

export class VirtualizedTableInputComponent extends React.PureComponent<
    Props,
    State
> {
    textInput = React.createRef<HTMLInputElement>();

    constructor(props: Props) {
        super(props);
        const { itemValue } = props;
        const inputValue =
            itemValue !== null && itemValue !== undefined
                ? itemValue.toString()
                : '';

        this.state = {
            initialItemValue: itemValue,
            inputValue
        };
    }

    componentDidMount(): void {
        document.addEventListener('keydown', this.handleKeyPress);
    }

    componentWillUnmount(): void {
        document.removeEventListener('keydown', this.handleKeyPress);

        this.updateItemValue();

        const { errorMessage } = this.props;

        if (errorMessage) {
            this.rejectInput();
        }
    }

    get isFocused(): boolean {
        const { itemKey } = this.props;
        const activeElementId = get(document, ['activeElement', 'id']);
        return activeElementId === itemKey;
    }

    rejectInput = (): void => {
        const { itemKey, columnKey, errorMessage, ...props } = this.props;
        const { initialItemValue } = this.state;

        props.setItemValue(itemKey, columnKey, initialItemValue, {
            ignoreValidation: true
        });
    };

    /**
     * Force input foucus when the cell is active and the user starts typing.
     */
    handleKeyPress = (): void => {
        this.focusTextInput();
    };

    focusTextInput = (): void => {
        const { current } = this.textInput;

        if (current && !this.isFocused) {
            current.focus({
                preventScroll: true
            });
        }
    };

    updateItemValue = (): void => {
        const {
            itemKey,
            columnKey,
            onItemValueChange,
            errorMessage,
            number
        } = this.props;
        const { inputValue, initialItemValue } = this.state;

        const nextItemValue = number
            ? toNumber(inputValue)
            : toNull(inputValue);

        if (
            nextItemValue !== initialItemValue &&
            !errorMessage &&
            onItemValueChange
        ) {
            onItemValueChange(itemKey, columnKey, nextItemValue);
        }
    };

    handleChange = (event: React.FocusEvent<HTMLInputElement>): void => {
        const { itemKey, columnKey, number, ...props } = this.props;

        const {
            target: { value }
        } = event;

        this.setState(
            {
                inputValue: value
            },
            () => {
                const nextValue = number ? toNumberIfValueNotNaN(value) : value;
                props.setItemValue(itemKey, columnKey, nextValue);
            }
        );
    };

    render(): React.ReactElement {
        const { itemKey } = this.props;
        const { inputValue } = this.state;
        return (
            <input
                id={itemKey}
                ref={this.textInput}
                value={inputValue}
                onChange={this.handleChange}
                type="text"
            />
        );
    }
}

const mapStateToProps = createStructuredSelector<
    AppState,
    OwnProps,
    ConnectedProps
>({
    itemValue: makeSelectVirtualizedTableItemValue(),
    errorMessage: makeSelectVirtualizedTableErrorMessage()
});

export default connect(mapStateToProps, { setItemValue })(
    VirtualizedTableInputComponent
);
